import React from "react";
import { StyleSheet, View } from "react-native";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Clock, KeyRound, RefreshCcw, TriangleAlert, Users, XCircle } from "lucide-react";
import Pill from "../../../common/Pill/Pill";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import CustomDropdown from "../../../common/general/CustomDropDown/CustomDropdown";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomTextInput from "../../../common/general/CustomTextInput/CustomTextInput";
import { PILL_TYPES } from "./ConnectorDetails";
import type {
  ConnectorCredentialConfig,
  ConnectorCredentialField,
  InstitutionalConnectionSummary,
} from "../catalog.utils";

const KALTURA_APP_TOKEN_HASH_TYPE_FIELD_KEY = "appTokenHashType";
const KALTURA_APP_TOKEN_HASH_TYPE_OPTIONS = [
  { label: "SHA1", value: "SHA1" },
  { label: "SHA256", value: "SHA256" },
  { label: "SHA512", value: "SHA512" },
  { label: "MD5", value: "MD5" },
];

const getDefaultCredentialValue = (field: ConnectorCredentialField): string =>
  field.key === KALTURA_APP_TOKEN_HASH_TYPE_FIELD_KEY ? "SHA1" : "";

interface InstitutionalConnectedProps {
  connection: InstitutionalConnectionSummary;
  isSyncing: boolean;
  isReauthenticating: boolean;
  onSyncNow: () => void;
  onReauthenticate: () => void;
  syncActionLabel?: string;
  syncPendingLabel?: string;
  showSyncAction?: boolean;
  disableSyncAction?: boolean;
}

export function InstitutionalConnected({
  connection,
  isSyncing,
  isReauthenticating,
  onSyncNow,
  onReauthenticate,
  syncActionLabel = "Refresh Status",
  syncPendingLabel = "Refreshing...",
  showSyncAction = true,
  disableSyncAction = false,
}: InstitutionalConnectedProps) {
  const isFailed = connection.status === "failed" || connection.status === "expired";
  const isDegraded = connection.status === "degraded";
  const stats = [
    connection.lastSyncAt
      ? {
          key: "lastSyncAt",
          icon: <Clock size={12} color="#667085" />,
          label: "Last queried",
          value: connection.lastSyncAt,
        }
      : null,
    typeof connection.userCount === "number"
      ? {
          key: "userCount",
          icon: <Users size={12} color="#667085" />,
          label: "Users",
          value: connection.userCount.toLocaleString(),
        }
      : null,
    connection.authenticatedBy
      ? {
          key: "authenticatedBy",
          icon: null,
          label: "Authenticated by",
          value: connection.authenticatedBy,
        }
      : null,
    connection.authenticatedAt
      ? {
          key: "authenticatedAt",
          icon: null,
          label: "Connected",
          value: connection.authenticatedAt,
        }
      : null,
    connection.scope
      ? {
          key: "scope",
          icon: null,
          label: "Scope",
          value: connection.scope,
        }
      : null,
  ].filter((stat): stat is { key: string; icon: React.ReactNode; label: string; value: string } => stat !== null);

  return (
    <View style={styles.connectedContainer}>
      {isFailed && connection.errorMessage && (
        <Pill
          text={connection.errorMessage}
          backgroundColor={PILL_TYPES.failed.backgroundColor}
          textColor={PILL_TYPES.failed.textColor}
          textSize="xs"
          updated={true}
          borderColor={PILL_TYPES.failed.textColor}
          borderWidth={1}
          icon={<XCircle size={16} color={PILL_TYPES.failed.textColor} />}
          containerStyle={styles.alertPill}
          textStyle={styles.alertPillText}
        />
      )}
      {isDegraded && connection.errorMessage && (
        <Pill
          text={connection.errorMessage}
          backgroundColor={PILL_TYPES.degraded.backgroundColor}
          textColor={PILL_TYPES.degraded.textColor}
          textSize="xs"
          updated={true}
          borderColor={PILL_TYPES.degraded.textColor}
          borderWidth={1}
          icon={<TriangleAlert size={16} color={PILL_TYPES.degraded.textColor} />}
          containerStyle={styles.alertPill}
          textStyle={styles.alertPillText}
        />
      )}

      {stats.length > 0 && (
        <View style={styles.statsGrid}>
          {stats.map((stat) => (
            <View key={stat.key} style={styles.statCell}>
              <View style={styles.statLabelRow}>
                {stat.icon}
                <CustomText
                  text={stat.label}
                  size="xs"
                  useTranslationText={false}
                  textType="text"
                  weight="normal"
                  role="text"
                  style={styles.statLabel}
                  numberOfLines={1}
                />
              </View>
              <CustomText
                text={stat.value}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.statValue}
                numberOfLines={2}
              />
            </View>
          ))}
        </View>
      )}

      {(isFailed || isDegraded || showSyncAction) && (
        <View style={styles.actionRow}>
          {(isFailed || isDegraded) && (
            <CustomButton
              text={isReauthenticating ? "Reconnecting..." : "Re-authenticate"}
              size="m"
              onPress={onReauthenticate}
              loading={isReauthenticating}
              useTranslationText={false}
              disabled={isReauthenticating}
              leftIcon={<RefreshCcw size={12} color="#FFFFFF" />}
              styleType="primary"
              style={styles.actionButton}
              textStyle={styles.actionButtonText}
            />
          )}
          {showSyncAction && (
            <CustomButton
              text={isSyncing ? syncPendingLabel : syncActionLabel}
              size="m"
              onPress={onSyncNow}
              loading={isSyncing}
              disabled={isFailed || disableSyncAction}
              useTranslationText={false}
              leftIcon={<RefreshCcw size={12} color="#344054" />}
              styleType="primaryLight"
              style={styles.secondaryActionButton}
              textStyle={styles.secondaryActionButtonText}
            />
          )}
        </View>
      )}
    </View>
  );
}

interface InstitutionalSetupProps {
  fields: readonly ConnectorCredentialField[];
  onSubmitRef?: (submit: () => Promise<{ config: ConnectorCredentialConfig; isValid: boolean }>) => void;
  onConfigChange?: (config: ConnectorCredentialConfig) => void;
}

export function InstitutionalSetup({ fields, onSubmitRef, onConfigChange }: InstitutionalSetupProps) {
  const {
    control,
    getValues,
    trigger,
    formState: { errors },
  } = useForm<ConnectorCredentialConfig>({
    mode: "onChange",
    defaultValues: Object.fromEntries(fields.map((field) => [field.key, getDefaultCredentialValue(field)])),
  });
  const watchedValues = useWatch({ control });
  const lastEmittedConfigKeyRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!onConfigChange) {
      return;
    }

    const nextConfig = Object.fromEntries(
      fields.map((field) => [field.key, typeof watchedValues?.[field.key] === "string" ? watchedValues[field.key] : ""])
    );
    const nextConfigKey = JSON.stringify(nextConfig);

    if (lastEmittedConfigKeyRef.current === nextConfigKey) {
      return;
    }

    lastEmittedConfigKeyRef.current = nextConfigKey;
    onConfigChange(nextConfig);
  }, [fields, onConfigChange, watchedValues]);

  React.useEffect(() => {
    if (!onSubmitRef) {
      return;
    }

    onSubmitRef(async () => {
      const isValid = await trigger(fields.map((field) => field.key) as Array<keyof ConnectorCredentialConfig>);
      const config = getValues();

      return { config, isValid };
    });
  }, [fields, getValues, onSubmitRef, trigger]);

  return (
    <>
      <CustomText
        text="Authenticate"
        size="s"
        useTranslationText={false}
        textType="text"
        weight="bold"
        role="text"
        style={styles.sectionTitle}
      />
      <View style={styles.formFields}>
        {fields.map((field) => (
          <View key={field.key}>
            <View style={styles.fieldLabelRow}>
              <KeyRound size={14} color="#667085" />
              <CustomText
                text={field.required ? `${field.label} *` : field.label}
                size="s"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.fieldLabel}
              />
            </View>
            {field.description ? (
              <CustomText
                text={field.description}
                size="xs"
                useTranslationText={false}
                textType="text"
                weight="normal"
                role="text"
                style={styles.fieldDescription}
              />
            ) : null}
            <Controller
              control={control}
              name={field.key}
              rules={{
                validate: (value) => (!field.required || value.trim().length > 0 ? true : `${field.label} is required`),
              }}
              render={({ field: formField }) =>
                field.key === KALTURA_APP_TOKEN_HASH_TYPE_FIELD_KEY ? (
                  <CustomDropdown
                    options={KALTURA_APP_TOKEN_HASH_TYPE_OPTIONS}
                    value={formField.value ?? "SHA1"}
                    onChange={formField.onChange}
                    placeholder="Select hash type"
                    accessibilityLabel="App Token Hash Type"
                    error={errors[field.key]?.message}
                    containerStyle={styles.credentialDropdown}
                    dropdownStyle={styles.credentialDropdownField}
                  />
                ) : (
                  <CustomTextInput
                    placeholder={`Enter ${field.label.toLowerCase()}`}
                    value={formField.value ?? ""}
                    onChangeText={formField.onChange}
                    secureTextEntry={field.isSecret}
                    error={errors[field.key]?.message}
                    style={styles.credentialInput}
                  />
                )
              }
            />
          </View>
        ))}
      </View>
    </>
  );
}

const styles = StyleSheet.create({
  connectedContainer: {
    marginTop: 20,
    gap: 20,
  },
  alertPill: {
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 8,
    alignSelf: "flex-start",
    width: "100%",
  },
  alertPillText: {
    marginLeft: 4,
  },
  statsGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  statCell: {
    width: "47%",
    gap: 4,
  },
  statLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  statLabel: {
    color: "#667085",
  },
  statValue: {
    color: "#344054",
  },
  actionRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  actionButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  actionButtonText: {
    fontSize: 12,
    fontWeight: "500",
  },
  secondaryActionButton: {
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    width: "25%",
    borderWidth: 1,
    borderColor: "#0000001A",
  },
  secondaryActionButtonText: {
    fontSize: 12,
    fontWeight: "500",
    color: "#344054",
  },
  sectionTitle: {
    color: "#101828",
    marginTop: 20,
  },
  fieldLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 12,
    gap: 4,
  },
  fieldLabel: {
    color: "#101828",
    marginBottom: 6,
  },
  fieldDescription: {
    color: "#667085",
    marginTop: 4,
  },
  formFields: {
    gap: 12,
    marginTop: 12,
  },
  credentialInput: {
    marginTop: 4,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    height: 40,
    fontSize: 14,
  },
  credentialDropdown: {
    marginTop: 4,
  },
  credentialDropdownField: {
    minHeight: 40,
  },
});
